import React, { useEffect, useState } from 'react';
import { AlertTriangle, Award, BookOpen, CheckCircle2, ClipboardList, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { MetricCard } from '../common/MetricCard';
import { learningApi } from '../../utils/learning-api';
import {
    courseAssignmentStatusPillClass,
    formatLearningDate,
    initials,
    assignmentSourceLabel,
} from '../../utils/learning';
import { AssignmentRow, PersonLearningResponse } from '../../types/learning.types';

/**
 * The Learning tab on a person page (LEARNING.md §9).
 *
 * Read-only: assignments are managed from the Learning Register, not here. Gated upstream
 * by `read:person_learning`, and the backend narrows a manager to direct reports — this
 * component never decides who may see the data, it just renders what the API returns.
 */
interface PersonLearningTabProps {
    userId: string;
}

export const PersonLearningTab: React.FC<PersonLearningTabProps> = ({ userId }) => {
    const [data, setData] = React.useState<PersonLearningResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await learningApi.person.get(userId);
                if (!cancelled) setData(result);
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : 'Failed to load learning records';
                if (!cancelled) setError(message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [userId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                <span className="ml-2 text-sm text-muted-foreground">Loading learning records...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                <AlertTriangle className="h-6 w-6 text-destructive" />
                <p className="text-sm text-muted-foreground">{error}</p>
            </div>
        );
    }

    const rows: AssignmentRow[] = data?.rows || [];
    const stats = data?.stats;

    const renderStatus = (row: AssignmentRow) => (
        <div className="flex flex-wrap items-center gap-1">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${courseAssignmentStatusPillClass(row.status, row.overdue)}`}>
                {row.status}
            </span>
            {row.overdue && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium text-destructive border border-destructive/30 bg-destructive/5">
                    Overdue
                </span>
            )}
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Stat tiles */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <MetricCard title="Assigned" value={stats?.assigned ?? 0} icon={ClipboardList} iconClassName="bg-slate-500" />
                <MetricCard
                    title="In progress"
                    value={stats?.inProgress ?? 0}
                    icon={Loader2}
                    iconClassName="bg-amber-500"
                    valueClassName="text-amber-600"
                />
                <MetricCard
                    title="Completed"
                    value={stats?.completed ?? 0}
                    icon={CheckCircle2}
                    iconClassName="bg-emerald-500"
                    valueClassName="text-emerald-600"
                />
                <MetricCard
                    title="Overdue"
                    value={stats?.overdue ?? 0}
                    icon={AlertTriangle}
                    iconClassName="bg-red-500"
                    valueClassName="text-destructive"
                />
                <MetricCard title="Certificates" value={stats?.certificates ?? 0} icon={Award} iconClassName="bg-indigo-500" />
            </div>

            {/* Mobile: cards */}
            <div className="space-y-3 md:hidden">
                {rows.length === 0 ? (
                    <Card>
                        <CardContent className="py-8 text-center text-sm text-muted-foreground">
                            No learning records
                        </CardContent>
                    </Card>
                ) : (
                    rows.map((row) => (
                        <Card key={row.id}>
                            <CardContent className="space-y-2 p-4">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <BookOpen size={14} className="text-muted-foreground" />
                                        <span className="font-medium">{row.courseTitle}</span>
                                        {row.isOnboardingCourse && (
                                            <Badge variant="outline" className="text-xs">Onboarding</Badge>
                                        )}
                                    </div>
                                    {renderStatus(row)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {assignmentSourceLabel(row)} · Assigned {formatLearningDate(row.assignedDate)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Due {formatLearningDate(row.dueDate)}
                                    {row.certificateIssued && ' · Certificate issued'}
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            {/* Desktop: table */}
            <Card className="hidden md:block">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                        <ClipboardList size={18} className="text-blue-500" />
                        Learning records
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-3">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="font-semibold text-slate-900">Course</TableHead>
                                <TableHead className="font-semibold text-slate-900">Source</TableHead>
                                <TableHead className="font-semibold text-slate-900">Assigned</TableHead>
                                <TableHead className="font-semibold text-slate-900">Due</TableHead>
                                <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                <TableHead className="font-semibold text-slate-900">Certificate</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                                        No learning records
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>
                                            <div className="flex flex-wrap items-center gap-2">
                                                {row.category && (
                                                    <span
                                                        className="inline-block h-2 w-2 rounded-full"
                                                        style={{ backgroundColor: row.category.colour }}
                                                    />
                                                )}
                                                <span className="font-medium">{row.courseTitle}</span>
                                                {row.isOnboardingCourse && (
                                                    <Badge variant="outline" className="text-xs">Onboarding</Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {assignmentSourceLabel(row)}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {formatLearningDate(row.assignedDate)}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {formatLearningDate(row.dueDate)}
                                        </TableCell>
                                        <TableCell>{renderStatus(row)}</TableCell>
                                        <TableCell className="font-mono text-xs text-muted-foreground">
                                            {row.certificateIssued ? 'Issued' : '–'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
};

export default PersonLearningTab;
